import { Box, Button, Checkbox, NoticeBox } from '@factorialco/deprecated-design-system'
import { CustomFieldsFieldTypeEnum } from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import SelectField from 'modules/core/components/SelectField'
import TextArea from 'modules/core/components/TextArea'
import { useMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'
import { ModalStates } from 'modules/core/lib/questionModalStates'

import { FieldTypes } from 'modules/contracts_settings/components/ContractFields/Taxonomies/taxonomyTranslator'
import SingleChoice from 'modules/legal_entities/pages/settings/ContractsLegalEntityPage/ContractFields/CustomField/SingleChoice'
import { CustomFieldsField } from 'modules/legal_entities/pages/settings/ContractsLegalEntityPage/ContractFields/types'

type illustrationType = 'pencil' | 'server-edit' | 'delete'
interface Props {
  field?: CustomFieldsField
  duplicate?: boolean
  onClose: () => void
  onSubmit: () => void
  onInputChange: (value: string) => void
  onMandatoryChange: (value: boolean) => void
  onTypeChange: (value: string) => void
  onAddOption: () => void
  onOptionAction: (action: 'delete' | 'edit' | 'archive', index: number, value?: string) => void
  actionType?: ModalStates
  modalIllustration?: illustrationType
  modalTitle: string
  modalDescription?: string
  modalButton?: string
  canSubmit?: boolean
  isSubmitting?: boolean
}

type OptionsType = {
  label: string
  value: CustomFieldsFieldTypeEnum | undefined
}

const fieldTypeOptions = (): OptionsType[] => {
  const options = [
    {
      label: i18n.t('settings.custom_fields.new.labels.text'),
      value: FieldTypes.text,
    },
    {
      label: i18n.t('settings.custom_fields.new.labels.long_text'),
      value: FieldTypes.long_text,
    },
    {
      label: i18n.t('settings.custom_fields.new.labels.date'),
      value: FieldTypes.date,
    },
    {
      label: i18n.t('settings.custom_fields.new.labels.number'),
      value: FieldTypes.cents,
    },
    {
      label: i18n.t('settings.custom_fields.new.labels.single_choice'),
      value: FieldTypes.single_choice,
    },
  ]

  return options
}

const FieldModal = ({
  field,
  onClose,
  onSubmit,
  onInputChange,
  onMandatoryChange,
  onTypeChange,
  onAddOption,
  onOptionAction,
  modalIllustration,
  modalTitle,
  modalDescription,
  modalButton,
  duplicate = false,
  canSubmit = true,
  isSubmitting = false,
  actionType,
}: Props) => {
  const typeOptions = useMemo(() => fieldTypeOptions(), [])
  const existingField = !!field?.id
  const isDisabledToEdit = existingField && !duplicate

  return (
    <Modal
      title={modalTitle}
      description={modalDescription}
      illustration={modalIllustration}
      onClose={onClose}
      footer={
        <Button
          type="primary"
          submit
          disabled={!canSubmit || isSubmitting}
          loading={isSubmitting}
          iconName={isSubmitting && 'spinner'}
        >
          {modalButton}
        </Button>
      }
      onSubmit={onSubmit}
    >
      {actionType !== 'delete' && (
        <Box flexDirection="column" gap="s24">
          <TextArea
            label={i18n.t('settings.custom_fields.new.labels.title')}
            value={field?.labelText}
            onChange={onInputChange}
            mandatory
          />
          <Checkbox
            checked={field?.required ?? false}
            label={i18n.t('ats.custom_fields.label.required')}
            onChange={(event) => onMandatoryChange(event.target.checked)}
          />
          <SelectField
            label={i18n.t('settings.custom_fields.new.labels.field_type')}
            value={field?.fieldType}
            none={i18n.t('settings.custom_fields.new.labels.choose')}
            options={typeOptions}
            onChange={onTypeChange}
            readonly={isDisabledToEdit}
          />
          {field?.fieldType === FieldTypes.single_choice && !!field?.options && (
            <SingleChoice
              options={field?.options}
              onAddOption={onAddOption}
              onOptionAction={onOptionAction}
            />
          )}
          <NoticeBox
            color="champagne400"
            message={
              field
                ? i18n.t('settings.custom_fields.new.permissions_edit')
                : i18n.t('settings.custom_fields.new.permissions_new')
            }
          />
        </Box>
      )}
    </Modal>
  )
}

export default FieldModal
